"use client";

import { useBrowserPreference } from "@workspace/gamja-platform/hooks/useBrowserPreference";
import { readBrowserPreference } from "@workspace/gamja-platform/lib/browser-preference";
import { useSettings } from "@/components/settings/SettingsProvider";
import { useEffect, useState } from "react";
import MaintenanceNoticePopup from "@/components/MaintenanceNoticePopup";
import PreApplicationPopup from "@/components/PreApplicationPopup";
import YouTubeLivePopup from "@/components/YouTubeLivePopup";
import RestorationParticipationPopup from "@/components/RestorationParticipationPopup";
import type { MaintenanceNoticeStatus } from "@/lib/maintenance-notice";

const REFRESH_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 5_000;

function isMaintenanceNoticeStatus(value: unknown): value is MaintenanceNoticeStatus {
  if (!value || typeof value !== "object") return false;
  return (
    "phase" in value &&
    (value.phase === "hidden" || value.phase === "upcoming" || value.phase === "ongoing") &&
    "dayKey" in value &&
    typeof value.dayKey === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.dayKey)
  );
}

export default function HomeNoticePopups() {
  const [enabled] = useBrowserPreference("jcg:notice-popups", true);
  const { section } = useSettings();
  const [status, setStatus] = useState<MaintenanceNoticeStatus | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    // Hydration starts with the server default; check the stored opt-out before fetching.
    if (!enabled || readBrowserPreference("jcg:notice-popups") === "false") return;

    let disposed = false;
    let controller: AbortController | null = null;

    async function refresh() {
      if (controller) return;
      const requestController = new AbortController();
      controller = requestController;
      const timeout = window.setTimeout(() => requestController.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch("/api/maintenance-notice", {
          cache: "no-store",
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error("공지 조회 실패");
        const nextStatus: unknown = await response.json();
        if (!isMaintenanceNoticeStatus(nextStatus)) throw new Error("공지 응답 형식 오류");
        if (!disposed) setStatus(nextStatus);
      } catch {
        if (!disposed) setStatus(null);
      } finally {
        window.clearTimeout(timeout);
        controller = null;
        if (!disposed) setResolved(true);
      }
    }

    function refreshWhenVisible() {
      if (document.visibilityState === "visible") void refresh();
    }

    void refresh();
    const interval = window.setInterval(refreshWhenVisible, REFRESH_INTERVAL_MS);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      disposed = true;
      controller?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [enabled]);

  if (!enabled || section) return null;

  return (
    <>
      {resolved && (
        <>
          <YouTubeLivePopup />
          <PreApplicationPopup />
          {status?.phase === "hidden" && <RestorationParticipationPopup />}
        </>
      )}
      {/* 자료 업데이트 팝업(PdfUpdatePopup)은 2026-09-17 JCG-3598 에서 제거했다. 점검 안내는
          공지 조회가 끝난 뒤 status 로만 열리며, 다른 팝업 위(z=60/61)에 표시된다. */}
      <MaintenanceNoticePopup status={resolved ? status : null} />
    </>
  );
}
